import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { PostTableRow } from '@app/models/post/PostResponse';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { useMounted } from '@app/hooks/useMounted';
import { Space, Tooltip, Button, Popconfirm } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notificationController } from '@app/controllers/notificationController';
import { TablePaginationConfig } from 'antd';
import FilterComponent from '@app/components/TableFilter/TableFilter';
import { Pagination } from '@app/models/pagination.model';
import { NomPayTableRow } from '@app/models/nominal_payments/NominalPaymentResponse';
import { getNomPayData } from '@app/api/nominalPayments.api';

const initialPagination: Pagination = {
  current: 1,
  pageSize: 5,
};

export const NominalPaymentsTable: React.FC = () => {
  const [tableData, setTableData] = useState<{
    data: NomPayTableRow[];
    pagination: Pagination;
    loading: boolean;
  }>({
    data: [],
    pagination: initialPagination,
    loading: false,
  });

  const { isMounted } = useMounted();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<{ [key: string]: string | [string, string] | null }>({});
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[] | null>(null);

  const { t } = useTranslation();

  const fetch = useCallback(
    (pagination: Pagination, activeFilters = filters) => {
      setTableData((prev) => ({ ...prev, loading: true }));
      const cleanFilters = sanitizeFilters(activeFilters);

      getNomPayData({ ...pagination, ...cleanFilters })
        .then((res) => {
          if (isMounted.current) {
            setTableData({
              data: res.data,
              pagination: {
                current: res.pagination.current,
                pageSize: res.pagination.pageSize,
                total: res.pagination.total,
              },
              loading: false,
            });
            setStatusOptions(res.status_options);
          }
        })
        .catch((error) => {
          notificationController.error({ message: error.message });
          setTableData({ data: [], pagination: initialPagination, loading: false });
        });
    },
    [filters, isMounted],
  );

  const sanitizeFilters = (filters: Record<string, any>) => {
    const cleanFilters: Record<string, any> = {};
    for (const key in filters) {
      if (filters[key] !== null && filters[key] !== '') {
        cleanFilters[key] = filters[key];
      }
    }
    return cleanFilters;
  };

  useEffect(() => {
    fetch(initialPagination);
  }, [fetch]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    fetch({
      ...filters,
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 5,
    });
  };

  const handleViewRow = (id: number) => navigate('/view-nominal-payment', { state: { id } });
  const handleEditRow = (id: number) => navigate('/edit-nominal-payment', { state: { id } });

  const showTotal = (total: number, range: [number, number]): React.ReactNode => (
    <span style={{ fontSize: '14px', marginRight: '20px', color: '#adadad' }}>
      {`Showing ${range[0]} - ${range[1]} of ${total} Nominal Payments`}
    </span>
  );

  const searchData: {
    name: string;
    placeholder: string;
    type: 'text' | 'dropdown';
    options?: Record<string, { label: string; value: string }[]>;
  }[] = [
    { name: 'search', placeholder: 'Search', type: 'text' },
    {
      name: 'status',
      placeholder: 'status',
      type: 'dropdown',
      options: {
        statusOptions: statusOptions || [],
      },
    },
  ];

  const columns: ColumnsType<PostTableRow> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
    },
    // {
    //   title: 'Customer',
    //   dataIndex: 'customer_id',
    //   key: 'customer_id',
    // },
    // {
    //   title: 'Department',
    //   dataIndex: 'department_id',
    //   key: 'customer_id',
    // },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              type="text"
              onClick={() => handleViewRow(record.id)}
              icon={<EyeOutlined />}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          {/* <Tooltip title="Edit">
            <Button
              type="text"
              onClick={() => handleEditRow(record.id)}
              icon={<EditOutlined />}
              style={{ color: '#52c41a' }}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  return (
    <BaseCard id="nominal-payments-table" title="Nominal Payments" padding="1.25rem">
      <FilterComponent
        data={searchData}
        onFilter={(newFilters) => {
          setFilters(newFilters);
          fetch({ current: 1, pageSize: tableData.pagination.pageSize ?? 10 }, newFilters);
        }}
      />
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: tableData.pagination.current,
          pageSize: tableData.pagination.pageSize,
          total: tableData.pagination.total,
          showTotal,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: true }}
      />
    </BaseCard>
  );
};
